import { Controller } from "@hotwired/stimulus";

type StripeCardElement = {
  mount(target: HTMLElement): void;
  on(eventName: "change", callback: (event: StripeElementChangeEvent) => void): void;
};

type StripeElementStyle = Record<string, string | Record<string, string>>;

type StripeCardElementOptions = {
  hidePostalCode?: boolean;
  style?: {
    base?: StripeElementStyle;
    invalid?: StripeElementStyle;
  };
};

type StripeElements = {
  create(type: "card", options?: StripeCardElementOptions): StripeCardElement;
};

type StripePaymentMethodResult = {
  error?: { message?: string };
  paymentMethod?: { id: string };
};

type StripeElementChangeEvent = {
  error?: { message?: string };
};

type StripeClient = {
  elements(): StripeElements;
  createPaymentMethod(options: {
    type: "card";
    card: StripeCardElement;
    billing_details?: {
      name?: string;
      email?: string;
    };
  }): Promise<StripePaymentMethodResult>;
};

declare global {
  interface Window {
    Stripe?: (publicKey: string) => StripeClient;
  }
}

export default class extends Controller<HTMLFormElement> {
  static targets = ["card", "errors", "paymentMethod", "submit", "name", "email"];
  static values = {
    publicKey: String,
    missingKeyMessage: String,
    defaultText: String,
    processingText: String,
  };

  declare readonly cardTarget: HTMLDivElement;
  declare readonly errorsTarget: HTMLElement;
  declare readonly paymentMethodTarget: HTMLInputElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly nameTarget: HTMLInputElement;
  declare readonly emailTarget: HTMLInputElement;
  declare readonly publicKeyValue: string;
  declare readonly missingKeyMessageValue: string;
  declare readonly defaultTextValue: string;
  declare readonly processingTextValue: string;
  declare readonly hasPublicKeyValue: boolean;

  private stripe?: StripeClient;
  private card?: StripeCardElement;

  connect() {
    if (!this.hasPublicKeyValue || !this.publicKeyValue || !window.Stripe) {
      this.showError(this.missingKeyMessageValue || "Stripe is not configured.");
      this.submitTarget.disabled = true;
      return;
    }

    this.stripe = window.Stripe(this.publicKeyValue);
    const elements = this.stripe.elements();
    this.card = elements.create("card", {
      hidePostalCode: true,
      style: this.cardStyle(),
    });

    this.card.on("change", (event) => {
      this.showError(event.error?.message || "");
    });

    this.card.mount(this.cardTarget);
  }

  async submit(event: Event) {
    if (this.paymentMethodTarget.value) return;

    event.preventDefault();
    if (!this.stripe || !this.card) return;

    this.setProcessing(true);
    this.showError("");

    const result = await this.stripe.createPaymentMethod({
      type: "card",
      card: this.card,
      billing_details: {
        name: this.nameTarget.value,
        email: this.emailTarget.value,
      },
    });

    if (result.error || !result.paymentMethod) {
      this.showError(result.error?.message || "We couldn't process that card. Please try again.");
      this.setProcessing(false);
      return;
    }

    this.paymentMethodTarget.value = result.paymentMethod.id;
    this.element.requestSubmit();
  }

  private cardStyle() {
    const rootStyles = getComputedStyle(document.documentElement);
    const cssVar = (name: string, fallback: string) => rootStyles.getPropertyValue(name).trim() || fallback;

    const textColor = cssVar("--color-base-content", "#000000");
    const errorColor = cssVar("--color-error", "#d21f07");
    const mutedTextColor = this.withAlpha(textColor, 0.48);

    return {
      base: {
        color: textColor,
        iconColor: mutedTextColor,
        fontFamily: '"Satoshi", system-ui, sans-serif',
        fontSize: "16px",
        fontWeight: "500",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: mutedTextColor,
        },
      },
      invalid: {
        color: errorColor,
        iconColor: errorColor,
      },
    };
  }

  private withAlpha(color: string, alpha: number) {
    const hex = color.match(/^#([0-9a-f]{6})$/i)?.[1];
    if (!hex) return color;

    const [r, g, b] = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private setProcessing(processing: boolean) {
    this.submitTarget.disabled = processing;
    this.submitTarget.textContent = processing ? this.processingTextValue : this.defaultTextValue;
  }

  private showError(message: string) {
    this.errorsTarget.textContent = message;
  }
}
